import { useCallback, useEffect, useRef, useState } from "react";
import { BMI_ERROR_MESSAGE, ONLY_NUMBERS } from "@/constants";
import { calculateBMI } from "@/utils";
import { CSSTransition } from "react-transition-group";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { Button, ButtonType } from "@/components/common/button";
import { DEFAULT_RANGE } from "@/utils/validateField/weight";

type HeightWeightAnswer = {
  feet: string;
  inches: string;
  weight: string;
};

type Validators = Record<InputKey, (value: string) => string>;

enum InputKey {
  FEET = "feet",
  WEIGHT = "weight",
  INCHES = "inches",
}

const generalValidator = (min: number, max: number) => (value: string) => {
  if (Number(value) < min) {
    return String(min);
  }
  if (Number(value) > max) {
    return String(max);
  }
  return value;
};

const validators: Validators = {
  [InputKey.FEET]: generalValidator(3, 8),
  [InputKey.WEIGHT]: generalValidator(DEFAULT_RANGE.min, DEFAULT_RANGE.max),
  [InputKey.INCHES]: generalValidator(0, 11),
};

const BodyMeasurementInput = ({
  label,
  unit,
  value,
  onChange,
  onBlur,
  onFocus,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: (v: string) => void;
  onFocus?: (v: string) => void;
}) => {
  return (
    <div className="w-full flex-1">
      <label className="text-sm mb-2">{label}</label>
      <div className="relative flex items-center">
        <input
          value={value}
          onChange={({ target }) => onChange(target.value)}
          onBlur={({ target }) => onBlur && onBlur(target.value)}
          onFocus={({ target }) => onFocus && onFocus(target.value)}
          type="text"
          className="p-2 sm:p-3 text-lg sm:text-xl border-[3px] w-full border-soft rounded-lg focus:border-medium-green focus:outline-none"
          inputMode="tel"
        />
        {unit && (
          <div className="absolute right-1 pr-5 pl-2 py-2 bg-white">{unit}</div>
        )}
      </div>
    </div>
  );
};

const NUMBER_OF_ANSWERS = 3;

const HeightWeightInputsQuestion = ({
  question,
  questionValue,
  setQuestionValue,
  setQuestionAnswer,
}: QuizQuestionProps<HeightWeightAnswer>) => {
  const [errorMessage, setErrorMessage] = useState("");
  const errorMessageRef = useRef(null);

  const isAllInputsFilled =
    Object.values(questionValue).filter(Boolean).length === NUMBER_OF_ANSWERS;

  useEffect(() => {
    // reset error on any input
    setErrorMessage("");
  }, [questionValue]);

  const onSubmitAnswer = () => {
    const { weight, feet, inches } = questionValue;
    const bmi = calculateBMI(+weight, +feet, +inches);

    if (bmi < 27) {
      setErrorMessage(BMI_ERROR_MESSAGE);
      return;
    }

    setErrorMessage("");

    setQuestionAnswer(questionValue);
  };

  const handleInputChange = useCallback(
    (inputValue: string, inputKey: string) => {
      let cleanedInput = inputValue.replace(ONLY_NUMBERS, "");
      setQuestionValue({ ...questionValue, [inputKey]: cleanedInput });
    },
    [questionValue]
  );

  const handleBlur = useCallback(
    (inputValue: string, inputKey: string) => {
      if (inputValue === "") {
        return;
      }
      let cleanedInput = Number(inputValue).toString();
      cleanedInput = validators[inputKey as InputKey](cleanedInput);
      setQuestionValue({ ...questionValue, [inputKey]: cleanedInput });
    },
    [questionValue]
  );

  return (
    <div>
      <div className="mb-2 w-full flex justify-between items-center gap-2.5 sm:gap-3 lg:gap-4">
        <BodyMeasurementInput
          label="Feet"
          unit="Ft."
          value={questionValue["feet"]}
          onChange={(v: string) => handleInputChange(v, InputKey.FEET)}
          onBlur={(v) => handleBlur(v, InputKey.FEET)}
        />

        <BodyMeasurementInput
          label="Inches"
          unit="In."
          value={questionValue["inches"]}
          onChange={(v: string) => handleInputChange(v, InputKey.INCHES)}
          onBlur={(v) => handleBlur(v, InputKey.INCHES)}
        />
      </div>

      <BodyMeasurementInput
        label="Current weight (lbs.)"
        unit="Lbs."
        value={questionValue["weight"]}
        onChange={(v: string) => handleInputChange(v, InputKey.WEIGHT)}
        onBlur={(v) => handleBlur(v, InputKey.WEIGHT)}
      />

      <CSSTransition
        nodeRef={errorMessageRef}
        in={!!errorMessage}
        timeout={300}
        classNames="fade"
        unmountOnExit
      >
        <div
          ref={errorMessageRef}
          className="mt-4 text-brick-red font-medium text-sm sm:text-base"
        >
          {errorMessage}
        </div>
      </CSSTransition>

      <Button
        type={ButtonType.NEXT}
        disabled={!isAllInputsFilled || !!errorMessage}
        onClick={onSubmitAnswer}
      />
    </div>
  );
};

export { HeightWeightInputsQuestion };
